import React from 'react';
import PropTypes from 'prop-types';
import {withApollo} from '@apollo/client/react/hoc';
import moment from 'moment';

import {FiltersWrapper} from '@appComponents/HOCFiltersWrapper';
import {
    onSubmit,
    updateFilterState,
    getMaxMinDate,
    updateDateState,
    handleOnEventTypeChange,
    handleOnSportChange,
    handleOnTournamentCategoryChange,
    handleOnContentCategoryLevel1Change,
    handleOnContentCategoryLevel2Change,
    fetchTournamentCategories,
    fetchTournaments,
    fetchContentCategoryLevel2,
    fetchContentCategoryLevel3,
    fetchSports,
    fetchContentCategoriesLevel1,
    fetchFirstStageCategories,
    fetchSecondStageCategories,
    handleOnFirstStageCategoryChange,
} from '@modules/booking/utils/clientBookingsFiltersHelpers';
import {getTodayDate, getEndOfTodayDate} from '@utils/date';
import {cancelGraphqlDebouncedFetches, createGraphqlDebouncedFetch} from '@utils/graphqlDebounce';

export function withClientBookingsFilters(WrappedComponent, filterName) {
    class ClientBookingsFiltersHOC extends React.Component {
        static propTypes = {
            additionalFilterId: PropTypes.string,
            client: PropTypes.object,
            data: PropTypes.object.isRequired,
            filters: PropTypes.object.isRequired,
            form: PropTypes.object,
            loading: PropTypes.bool.isRequired,
            restoreFilterValues: PropTypes.func.isRequired,
            restoreFilterDateValues: PropTypes.func.isRequired,
        };

        static defaultProps = {
            additionalFilterId: null,
        };

        state = {
            fromMaxDate: null,
            fromMaxDateDefault: null,
            fromMaxTime: moment().hours(23).minutes(59),
            toMinDate: null,
            toMinDateDefault: null,
            toMinTime: moment().hours(0).minutes(0),
            selectedContentCategoriesLevel1: [],
            contentCategoriesLevel1Loading: true,
            contentCategoriesLevel1: [],
            contentCategoriesLevel2: [],
            contentCategoriesLevel3: [],
            hasContentCategoryLevel1: false,
            hasContentCategoryLevel2: false,
            showSportFilter: false,
            hasSelectedContentCategoryLevel1: false,
            hasSelectedContentCategoryLevel2: false,
            hasSelectedSport: false,
            hasSelectedTournamentCategory: false,
            selectedContentCategoriesLevel2: [],
            selectedContentCategoriesLevel3: [],
            selectedTournamentCategories: [],
            selectedTournaments: [],
            tournamentCategories: [],
            tournaments: [],
            selectedSports: [],
            sports: [],
            sportsLoading: true,
            isStageEvent: false,
            hasSelectedFirstStageCategory: false,
            firstStageCategories: [],
            selectedFirstStageCategories: [],
            secondStageCategories: [],
            selectedSecondStageCategories: [],
        };

        componentDidMount() {
            this.setState((prevState) =>
                updateFilterState(prevState, getMaxMinDate(this.props.filters, this.props.restoreFilterValues), this.setStateHandler)
            );
        }

        componentWillUnmount() {
            this.cancelDebouncedFetches();
        }

        createDependentOptionsFetch(fetchCallback, getFormHandler = form => form) {
            return createGraphqlDebouncedFetch((value, selectedValues) => {
                fetchCallback(value, getFormHandler(this.props.form), selectedValues, this.setStateHandler);
            });
        }

        cancelDebouncedFetches() {
            cancelGraphqlDebouncedFetches([
                this.fetchTournamentCategoriesDebounced,
                this.fetchTournamentsDebounced,
                this.fetchContentCategoryLevel2Debounced,
                this.fetchContentCategoryLevel3Debounced,
                this.fetchFirstStageCategoriesDebounced,
                this.fetchSecondStageCategoriesDebounced,
            ]);
        }

        fetchTournamentCategoriesDebounced = this.createDependentOptionsFetch(fetchTournamentCategories);

        fetchTournamentsDebounced = this.createDependentOptionsFetch(fetchTournaments);

        fetchContentCategoryLevel2Debounced = this.createDependentOptionsFetch(fetchContentCategoryLevel2);

        fetchContentCategoryLevel3Debounced = this.createDependentOptionsFetch(fetchContentCategoryLevel3);

        fetchFirstStageCategoriesDebounced = this.createDependentOptionsFetch(
            fetchFirstStageCategories,
            form => form.changeValue
        );

        fetchSecondStageCategoriesDebounced = this.createDependentOptionsFetch(
            fetchSecondStageCategories,
            form => form.changeValue
        );

        onEventTypeChange = (event, value) => {
            this.cancelDebouncedFetches();
            const state = handleOnEventTypeChange(event, value);
            this.setState(state);

            this.props.form.changeValue('sports', null);
            this.props.form.changeValue('tournamentCategories', null);
            this.props.form.changeValue('tournaments', null);
            this.props.form.changeValue('firstStageCategories', null);
            this.props.form.changeValue('secondStageCategories', null);

            fetchSports(value, this.setStateHandler);
            fetchContentCategoriesLevel1(value, this.props.form, this.setStateHandler);
        };

        onSportChange = (event, value) => {
            const state = handleOnSportChange(event, value);

            this.setStateHandler(state, () => {
                if (!this.state.isStageEvent) {
                    this.fetchTournamentCategoriesDebounced(value, this.state.selectedTournamentCategories);
                } else {
                    this.fetchFirstStageCategoriesDebounced(value, this.state.selectedFirstStageCategories);
                }
            });
        };

        onTournamentCategoryChange = (event, value) => {
            handleOnTournamentCategoryChange(event, value, this.setStateHandler, () => {
                this.fetchTournamentsDebounced(value, this.state.selectedTournaments);
            });
        };

        onTournamentChange = (event, value) => {
            this.setState(() => ({
                selectedTournaments: value,
            }));
        };

        onFirstStageCategoryChange = (event, value) => {
            const state = handleOnFirstStageCategoryChange(event, value);

            this.setStateHandler(state, () => {
                this.fetchSecondStageCategoriesDebounced(value, this.state.selectedSecondStageCategories);
            });
        };

        onSecondStageCategoryChange = (event, value) => {
            this.setState(() => ({
                selectedSecondStageCategories: value,
            }));
        };

        onContentCategoryLevel1Change = (event, value) => {
            handleOnContentCategoryLevel1Change(event, value, this.setStateHandler, () => {
                this.fetchContentCategoryLevel2Debounced(value, this.state.selectedContentCategoriesLevel2);
            });
        };

        onContentCategoryLevel2Change = (event, value) => {
            handleOnContentCategoryLevel2Change(event, value, this.setStateHandler, () => {
                this.fetchContentCategoryLevel3Debounced(value, this.state.selectedContentCategoriesLevel3);
            });
        };

        onContentCategoryLevel3Change = (event, value) => {
            this.setState(() => ({
                selectedContentCategoriesLevel3: value,
            }));
        };

        onChange = (date, name) => {
            this.setState((prevState) => updateDateState(prevState, date, name));
        };

        setStateHandler = (stateObject, callbackFunc = () => {}) => {
            this.setState(() => (stateObject), callbackFunc);
        };

        render() {
            const filtersProps = {
                ...this.props,
                state: this.state,
                clientFilters: this.props.filters[filterName + this.props.additionalFilterId],
                dateConfigProps: {
                    onChange: this.onChange,
                    fromMaxDate: this.state.fromMaxDate,
                    fromMaxDateDefault: this.state.fromMaxDateDefault,
                    fromMaxTime: this.state.fromMaxTime,
                    toMinDate: this.state.toMinDate,
                    toMinDateDefault: this.state.toMinDateDefault,
                    toMinTime: this.state.toMinTime,
                    restoreFilterValues: this.props.restoreFilterValues,
                    restoreFilterDateValues: this.props.restoreFilterDateValues,
                },
                methods: {
                    onEventTypeChange: this.onEventTypeChange,
                    onSportChange: this.onSportChange,
                    onTournamentCategoryChange: this.onTournamentCategoryChange,
                    onTournamentChange: this.onTournamentChange,
                    onFirstStageCategoryChange: this.onFirstStageCategoryChange,
                    onSecondStageCategoryChange: this.onSecondStageCategoryChange,
                    onContentCategoryLevel1Change: this.onContentCategoryLevel1Change,
                    onContentCategoryLevel2Change: this.onContentCategoryLevel2Change,
                    onContentCategoryLevel3Change: this.onContentCategoryLevel3Change,
                },
            };

            return (<WrappedComponent {...filtersProps}/>);
        }
    }

    ClientBookingsFiltersHOC.displayName = 'ClientBookingsFiltersHOC';

    return FiltersWrapper(withApollo(ClientBookingsFiltersHOC), {
        name: filterName,
        onSubmit: onSubmit,
        initialValues: {
            from: getTodayDate(),
            to: getEndOfTodayDate(),
            isCancelled: 0,
        },
    });
}
